export function hasOwnProperty<T, K extends PropertyKey>(object: T, property: K): object is T & Record<K, unknown> {
  return Object.prototype.hasOwnProperty.call(object, property);
}

export function isError(error: unknown): error is Error {
  if (error instanceof Error) {
    return true;
  }
  if (error === null || typeof error !== 'object') {
    return false;
  }
  return hasOwnProperty(error, 'name') && hasOwnProperty(error, 'message');
}

export function isErrorWithCode(error: unknown): error is Error & { code: string } {
  return hasOwnProperty(error, 'code') && typeof error.code === 'string';
}

export function getErrorMessage(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (isError(error)) {
    return error.message;
  }

  return 'An unknown error occurred';
}

export function getRandomNumberInRange(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function possessiveApostrophe(name: string, possession: string): string {
  if (name.at(-1) === 's') {
    return `${name}' ${possession}`;
  }
  return `${name}'s ${possession}`;
}

export function ok(value: unknown, message?: string | Error): asserts value {
  if (value) {
    return;
  }
  const errorMessage = message instanceof Error ? message.message : message;
  throw new Error(errorMessage);
}

/**
 * Returns an indefinite article ("a" or "an") followed by the original string.
 * Uses pronunciation-based heuristics (UK English bias).
 */
export function aOrAn(text: string): string {
  const word = text.trim();
  if (!word) {
    return text;
  }

  // First meaningful token (ignore punctuation)
  const token = word.match(/[\dA-Za-z]+(?:['-][\dA-Za-z]+)*/)?.[0] ?? word;

  const lower = token.toLowerCase();

  // Silent "h" → "an" (UK + US)
  if (lower.startsWith('hour') || lower.startsWith('honest') || lower.startsWith('heir') || lower.startsWith('honour') || lower.startsWith('honor')) {
    return `an ${word}`;
  }

  // Initialisms pronounced as letters: "MBA", "NHS", "FBI"
  // Letters that start with vowel sounds: F (eff), M (em), N (en), S (ess), X (ex), R (ar), L (el), H (aitch)
  if (/^[\dA-Z]+$/.test(token) && /[A-Z]/.test(token)) {
    if (/^[FHL-NRSX]/.test(token)) {
      return `an ${word}`;
    }
    return `a ${word}`;
  }

  // "you" sound → "a"
  if (
    lower.startsWith('uni') || // university
    lower.startsWith('use') || // user, used
    lower.startsWith('urol') || // urology
    lower.startsWith('euro') || // European
    lower.startsWith('eu') // eulogy
  ) {
    return `a ${word}`;
  }

  // "one"/"once" → w sound
  if (lower.startsWith('one') || lower.startsWith('once')) {
    return `a ${word}`;
  }

  // Default vowel rule
  if (/^[aeiou]/.test(lower)) {
    return `an ${word}`;
  }

  return `a ${word}`;
}
